package com.mgacreative.hotel.feature.admin.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.core.network.NetworkConstants
import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import com.mgacreative.hotel.domain.model.Department
import com.mgacreative.hotel.domain.repository.DepartmentRepository
import io.github.jan.supabase.createSupabaseClient
import io.github.jan.supabase.auth.Auth
import io.github.jan.supabase.auth.providers.builtin.Email
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.postgrest.Postgrest
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class AdminStaffViewModel(
    private val supabaseClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession,
    private val departmentRepository: DepartmentRepository
) : ViewModel() {

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private val _errorMessage = MutableStateFlow<String?>(null)
    val errorMessage: StateFlow<String?> = _errorMessage.asStateFlow()

    private val _successMessage = MutableStateFlow<String?>(null)
    val successMessage: StateFlow<String?> = _successMessage.asStateFlow()

    private val _staffList = MutableStateFlow<List<StaffMember>>(emptyList())
    val staffList: StateFlow<List<StaffMember>> = _staffList.asStateFlow()

    private val _departments = MutableStateFlow<List<Department>>(emptyList())
    val departments: StateFlow<List<Department>> = _departments.asStateFlow()

    init {
        fetchStaffList()
        fetchDepartments()
    }

    fun fetchDepartments() {
        viewModelScope.launch {
            try {
                departmentRepository.getDepartments().collect { deps ->
                    _departments.value = deps
                }
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }

    fun fetchStaffList() {
        val tenantId = tenantSession.currentTenantId ?: return
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val response = supabaseClient.client.postgrest["users"]
                    .select { filter { eq("tenant_id", tenantId) } }
                    .decodeList<StaffMember>()
                _staffList.value = response
            } catch (e: Exception) {
                e.printStackTrace()
                _errorMessage.value = "Personel listesi alınamadı: ${e.message}"
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun clearMessages() {
        _errorMessage.value = null
        _successMessage.value = null
    }

    fun createStaff(
        username: String,
        pass: String,
        departmentId: String?,
        roles: List<String>
    ) {
        val tenantId = tenantSession.currentTenantId
        if (tenantId == null) {
            _errorMessage.value = "Otel yetki bilgisi bulunamadı. Lütfen tekrar giriş yapın."
            return
        }
        
        viewModelScope.launch {
            _isLoading.value = true
            clearMessages()
            try {
                val cleanUsername = username.trim().lowercase().replace(" ", "")
                val cleanTenantId = tenantId.take(8)
                val fakeEmail = "$cleanUsername@$cleanTenantId.local"

                val tempClient = createSupabaseClient(
                    supabaseUrl = NetworkConstants.SUPABASE_URL,
                    supabaseKey = NetworkConstants.SUPABASE_ANON_KEY
                ) {
                    install(Auth) {
                        autoLoadFromStorage = false
                        alwaysAutoRefresh = false
                    }
                    install(Postgrest)
                }

                val session = tempClient.auth.signUpWith(Email) {
                    this.email = fakeEmail
                    this.password = pass
                }
                
                val newUserId = session?.id ?: tempClient.auth.currentUserOrNull()?.id 
                    ?: throw Exception("Kullanıcı kaydı oluşturulamadı. Şifrenin en az 6 karakter olduğunu kontrol edin.")

                @kotlinx.serialization.Serializable
                data class UserUpsert(
                    val id: String,
                    val tenant_id: String,
                    val email: String,
                    val roles: List<String>,
                    val department_id: String? = null
                )

                val userPayload = UserUpsert(
                    id = newUserId,
                    tenant_id = tenantId,
                    email = fakeEmail,
                    roles = roles,
                    department_id = departmentId
                )

                supabaseClient.client.postgrest["users"].upsert(userPayload)

                _successMessage.value = "Personel başarıyla oluşturuldu!\nGiriş E-postası: $fakeEmail"
                fetchStaffList()
                
            } catch (e: Exception) {
                e.printStackTrace()
                _errorMessage.value = e.message ?: "Personel eklenirken hata oluştu."
            } finally {
                _isLoading.value = false
            }
        }
    }
}

@kotlinx.serialization.Serializable
data class StaffMember(
    val id: String = "",
    val tenant_id: String? = null,
    val role_id: String? = null,
    val roles: List<String> = emptyList(),
    val department_id: String? = null,
    val email: String? = null,
    val full_name: String? = null
)
